import taxonomy from "./status-taxonomy.json";

export type IntentName = "success" | "warning" | "danger" | "info" | "neutral";

const DOMAINS = [
  "order",
  "payment",
  "promotion",
  "stock",
  "sync",
  "audit",
  "marketplace_connection",
  "review_reason",
  "push_mode",
  "push_result",
  "listing_status",
] as const;
export type StatusDomain = (typeof DOMAINS)[number];

type TaxonomyEntry = {
  code: string;
  label_en: string;
  label_th: string;
  intent: IntentName;
  _note?: string;
};

const indexed: Record<StatusDomain, Map<string, TaxonomyEntry>> = (() => {
  const out = {} as Record<StatusDomain, Map<string, TaxonomyEntry>>;
  const raw = taxonomy as unknown as Record<string, TaxonomyEntry[]>;
  for (const domain of DOMAINS) {
    const rows = raw[domain] ?? [];
    out[domain] = new Map(rows.map((e) => [e.code, e]));
  }
  return out;
})();

export function statusIntent(domain: StatusDomain, code: string): IntentName {
  const entry = indexed[domain]?.get(code);
  if (!entry) {
    if (import.meta.env.DEV) {
      console.warn(
        `[statusIntent] unknown ${domain}:${code} — falling back to 'neutral'. ` +
          `Known codes: ${listStatusCodes(domain).join(", ")}`,
      );
    }
    return "neutral";
  }
  return entry.intent;
}

/** Operator-facing label for a status code from the taxonomy (en/th). Falls back to the raw code. */
export function statusLabel(domain: StatusDomain, code: string, lang: string): string {
  const entry = indexed[domain]?.get(code);
  if (!entry) return code;
  return lang.startsWith("th") ? entry.label_th : entry.label_en;
}

export function isKnownStatus(domain: StatusDomain, code: string): boolean {
  return Boolean(indexed[domain]?.has(code));
}

export function listStatusCodes(domain: StatusDomain): readonly string[] {
  return [...(indexed[domain]?.keys() ?? [])];
}
